import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Search, Hammer, Pencil } from "lucide-react";
import { PRIORITY_BADGE, STATUS_BADGE, scoreToColor } from "@/lib/constants";
import { toast } from "sonner";

const STATUSES = ["not_started", "in_progress", "completed", "deferred"];

export default function Remediation() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "assessor"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([api.get("/remediation"), api.get("/assessments")]);
      setItems(r.data); setAssessments(a.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const aMap = useMemo(() => Object.fromEntries(assessments.map((a) => [a.id, a])), [assessments]);

  const filtered = items.filter((i) => {
    const q = search.toLowerCase();
    const m1 = !q || [i.title, i.subcategory_code, i.recommendation, i.responsible_owner].some((x) => x?.toLowerCase().includes(q));
    const m2 = priority === "all" || i.priority === priority;
    return m1 && m2;
  });

  const grouped = STATUSES.map((st) => ({
    status: st, items: filtered.filter((i) => i.status === st),
  }));

  return (
    <div data-testid="remediation-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Remediation Roadmap</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Roadmap & Gap Analysis</h1>
          <p className="text-sm text-zinc-500 mt-1">Plan and track remediation across all assessments.</p>
        </div>
        <div className="text-xs text-zinc-500 mono">{filtered.length} / {items.length} tasks</div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" data-testid="remediation-search"
            className="pl-10 bg-[#121212] border-zinc-800" />
        </div>
        <div className="flex gap-1 border border-zinc-800 rounded-md p-1 bg-[#121212]">
          {["all", "critical", "high", "medium", "low"].map((p) => (
            <button key={p} onClick={() => setPriority(p)} data-testid={`priority-filter-${p}`}
              className={`px-3 py-1.5 rounded text-xs transition-colors ${priority === p ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"}`}>
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Kanban */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="kanban-board">
        {grouped.map((col) => (
          <div key={col.status} className="rounded-md border border-zinc-800 bg-[#0A0A0A]/40" data-testid={`kanban-col-${col.status}`}>
            <div className="px-3 py-2.5 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className={`${STATUS_BADGE[col.status]} text-[10px]`}>{col.status.replace("_", " ").toUpperCase()}</Badge>
              </div>
              <span className="text-[10px] text-zinc-500 mono">{col.items.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[100px] max-h-[600px] overflow-y-auto">
              {col.items.map((i) => (
                <div key={i.id} onClick={() => canEdit && setEditing(i)}
                  data-testid={`task-${i.id}`}
                  className="p-3 rounded-md border border-zinc-800 bg-[#121212] hover:border-zinc-700 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] mono text-white font-medium">{i.subcategory_code}</span>
                    <Badge className={`${PRIORITY_BADGE[i.priority]} text-[9px]`}>{i.priority.toUpperCase()}</Badge>
                  </div>
                  <div className="text-[12px] text-zinc-300 mt-1.5 line-clamp-2">{i.title}</div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500">
                    <div className="flex items-center gap-2">
                      <span>Effort: {i.effort}</span>
                      {i.responsible_owner && <span>· {i.responsible_owner}</span>}
                    </div>
                    {i.target_date && <span className="mono">{i.target_date}</span>}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px]">
                    <span className="text-zinc-500">Score</span>
                    <div className="flex items-center gap-1 mono">
                      <span style={{ color: scoreToColor(i.current_score) }}>{i.current_score.toFixed(1)}</span>
                      <span className="text-zinc-600">→</span>
                      <span className="text-white">{i.target_score.toFixed(1)}</span>
                      <span className="text-orange-400 ml-1">+{i.gap.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {col.items.length === 0 && <div className="text-[11px] text-zinc-600 text-center py-4">No tasks</div>}
            </div>
          </div>
        ))}
      </div>

      <EditTaskDialog item={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function EditTaskDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) setForm({
      title: item.title, recommendation: item.recommendation, target_score: item.target_score,
      priority: item.priority, effort: item.effort, responsible_owner: item.responsible_owner || "",
      target_date: item.target_date || "", status: item.status,
    });
  }, [item]);
  if (!item) return null;
  const submit = async () => {
    setBusy(true);
    try { await api.patch(`/remediation/${item.id}`, form); toast.success("Updated"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-lg" data-testid="edit-task-dialog">
        <DialogHeader><DialogTitle>{item.subcategory_code} — Edit Task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-uppercase">Title</Label>
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
          <div>
            <Label className="label-uppercase">Recommendation</Label>
            <Textarea rows={3} value={form.recommendation || ""} onChange={(e) => setForm({ ...form, recommendation: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SelectField label="Priority" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} options={["critical", "high", "medium", "low"]} testid="task-priority" />
            <SelectField label="Effort" value={form.effort} onChange={(v) => setForm({ ...form, effort: v })} options={["low", "medium", "high"]} testid="task-effort" />
            <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} testid="task-status" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Owner</Label>
              <Input value={form.responsible_owner || ""} onChange={(e) => setForm({ ...form, responsible_owner: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
            </div>
            <div>
              <Label className="label-uppercase">Target Date</Label>
              <Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-white text-black hover:bg-zinc-200" data-testid="task-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-uppercase">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm text-white">
        {options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}
